import { DialogType } from 'components/core/Dialog/common/Dialog';
import { DialogFactory } from 'components/core/Dialog/common/DialogContext';
import { SetValuesOptions } from 'components/dataCreationForms/withInputForm';
import { useCallback, useState } from 'react';
import { IntlShape } from 'react-intl';
import { LabelledEntity } from 'shared/types/commonView';
import { CreatableUserRole, UserRole } from 'shared/types/userRole';
import { CommonTranslationKeys } from 'intl/keys';
import { UserTranslationKeys } from 'intl/keys';
import { formatTranslation } from 'intl';

const ADMIN_ROLE_DIALOG_KEY = {
  projectAdmin: {
    title: UserTranslationKeys.setAdminRoleDialogTitle,
    body: UserTranslationKeys.setAdminRoleDialogBody,
  },
  clientAdmin: {
    title: UserTranslationKeys.setClientAdminRoleDialogTitle,
    body: UserTranslationKeys.setClientAdminRoleDialogBody,
  },
} as const;

interface UseAdminSelectionProps {
  intl: IntlShape;
  createDialog: DialogFactory;
  processesValue: LabelledEntity;
  sitesValue: LabelledEntity;
  availableSites: LabelledEntity[];
  availableProcesses: LabelledEntity[];
  setValues: (key: string, value: any, options?: SetValuesOptions) => void;
}

interface UseAdminSelection {
  adminSelected?: CreatableUserRole;
  handleAdminSelection: (role?: CreatableUserRole) => void;
  handleAdminDeselection: () => void;
  setAdminSelected: (role?: CreatableUserRole) => void;
}

export const useAdminSelection = ({
  intl,
  createDialog,
  processesValue,
  sitesValue,
  availableSites,
  availableProcesses,
  setValues,
}: UseAdminSelectionProps): UseAdminSelection => {
  const [adminSelected, setAdminSelected] = useState<
    CreatableUserRole | undefined
  >();

  const handleAdminSelection = useCallback(
    (role: CreatableUserRole | undefined) => {
      const dialogKeys =
        ADMIN_ROLE_DIALOG_KEY[
          role === UserRole.projectAdmin ? 'projectAdmin' : 'clientAdmin'
        ];
      createDialog({
        title: formatTranslation(intl, dialogKeys.title),
        description: (
          <span>
            {formatTranslation(intl, dialogKeys.body)}
          </span>
        ),
        type: DialogType.info,
        submitLabel: CommonTranslationKeys.ok,
      }).then(() => {
        setAdminSelected(role);
        setValues('sites', availableSites);
        setValues('processes', availableProcesses);
      });
    },
    [
      createDialog,
      intl,
      processesValue,
      sitesValue,
      availableSites,
      availableProcesses,
      setValues,
    ]
  );

  const handleAdminDeselection = useCallback(() => {
    setAdminSelected(undefined);
    setValues('sites', sitesValue);
    setValues('processes', processesValue);
  }, [setValues, sitesValue, processesValue]);

  return {
    adminSelected,
    handleAdminSelection,
    handleAdminDeselection,
    setAdminSelected,
  };
};
